import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { useToast } from '../../contexts/ToastContext'
import AdminLayout from '../../components/admin/AdminLayout'
import ConfirmDialog from '../../components/admin/ConfirmDialog'
import { supabase } from '../../lib/supabase'
import {
  Search,
  Filter,
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  BookOpen,
  AlertCircle,
  X,
  FileEdit
} from 'lucide-react'

interface Course {
  id: string
  title: string
  description: string
  instructor_name: string
  level: string
  duration_hours: number
  rating: number
  total_students: number
  is_published: boolean
  created_at: string
}

export default function CoursesManagement() {
  const { hasPermission } = useAuth()
  const toast = useToast()
  const [courses, setCourses] = useState<Course[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterLevel, setFilterLevel] = useState<string>('all')
  const [filterPublished, setFilterPublished] = useState<string>('all')
  const [modalOpen, setModalOpen] = useState<'create' | 'edit' | null>(null)
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null)
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean
    courseId: string | null
  }>({ isOpen: false, courseId: null })

  useEffect(() => {
    loadCourses()
  }, [])

  const loadCourses = async () => {
    try {
      setLoading(true)
      const { data: { user } } = await supabase.auth.getUser()
      if (!user?.id) throw new Error('No se pudo obtener el ID del usuario')
      
      // Usar la función RPC segura para cursos con UUID
      const { data, error } = await supabase
        .rpc('get_courses_for_management_by_uuid', {
          requesting_user_uuid: user.id
        })

      if (error) throw error
      setCourses(data || [])
    } catch (error: any) {
      console.error('Error loading courses:', error)
      toast.error(`Error al cargar cursos: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleTogglePublish = async (courseId: string, currentStatus: boolean) => {
    if (!hasPermission('publish_course')) {
      toast.error('No tienes permisos para publicar/despublicar cursos')
      return
    }

    try {
      const { error } = await supabase
        .from('courses')
        .update({ is_published: !currentStatus })
        .eq('id', courseId)

      if (error) throw error

      toast.success(currentStatus ? 'Curso despublicado' : 'Curso publicado exitosamente')
      loadCourses()
    } catch (error: any) {
      console.error('Error toggling publish:', error)
      toast.error(`Error: ${error.message}`)
    }
  }

  const handleDeleteCourse = async (courseId: string) => {
    if (!hasPermission('delete_course')) {
      toast.error('No tienes permisos para eliminar cursos')
      return
    }

    setConfirmDialog({ isOpen: true, courseId })
  }

  const confirmDelete = async () => {
    if (!confirmDialog.courseId) return

    try {
      const { error } = await supabase
        .from('courses')
        .delete()
        .eq('id', confirmDialog.courseId)

      if (error) throw error

      toast.success('Curso eliminado exitosamente')
      loadCourses()
    } catch (error: any) {
      console.error('Error deleting course:', error)
      toast.error(`Error: ${error.message}`)
    } finally {
      setConfirmDialog({ isOpen: false, courseId: null })
    }
  }

  const handleCreateCourse = async (courseData: any) => {
    if (!hasPermission('create_course')) {
      toast.error('No tienes permisos para crear cursos')
      return
    }

    try {
      const { error } = await supabase
        .from('courses')
        .insert(courseData)

      if (error) throw error

      toast.success('Curso creado exitosamente')
      loadCourses()
      setModalOpen(null)
    } catch (error: any) {
      console.error('Error creating course:', error)
      toast.error(`Error: ${error.message}`)
    }
  }

  const handleUpdateCourse = async (courseId: string, courseData: any) => {
    if (!hasPermission('manage_courses')) {
      toast.error('No tienes permisos para editar cursos')
      return
    }

    try {
      const { error } = await supabase
        .from('courses')
        .update(courseData)
        .eq('id', courseId)

      if (error) throw error

      toast.success('Curso actualizado exitosamente')
      loadCourses()
      setModalOpen(null)
      setSelectedCourse(null)
    } catch (error: any) {
      console.error('Error updating course:', error)
      toast.error(`Error: ${error.message}`)
    }
  }

  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         course.instructor_name?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesLevel = filterLevel === 'all' || course.level === filterLevel
    const matchesPublished = filterPublished === 'all' || 
                            (filterPublished === 'published' && course.is_published) ||
                            (filterPublished === 'unpublished' && !course.is_published)
    return matchesSearch && matchesLevel && matchesPublished
  })

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Gestión de Cursos
            </h1>
            <p className="text-gray-600">
              Administra el contenido educativo de la plataforma
            </p>
          </div>
          {hasPermission('create_course') && (
            <button
              onClick={() => setModalOpen('create')}
              className="flex items-center space-x-2 px-4 py-2 bg-education text-white rounded-lg hover:bg-education/90"
            >
              <Plus className="h-4 w-4" />
              <span>Nuevo Curso</span>
            </button>
          )}
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar cursos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-education focus:border-transparent"
              />
            </div>

            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <select
                value={filterLevel}
                onChange={(e) => setFilterLevel(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-education focus:border-transparent appearance-none"
              >
                <option value="all">Todos los niveles</option>
                <option value="principiante">Principiante</option>
                <option value="intermedio">Intermedio</option>
                <option value="avanzado">Avanzado</option>
              </select>
            </div>

            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <select
                value={filterPublished}
                onChange={(e) => setFilterPublished(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-education focus:border-transparent appearance-none"
              >
                <option value="all">Todos los estados</option>
                <option value="published">Publicados</option>
                <option value="unpublished">No publicados</option>
              </select>
            </div>
          </div>

          <div className="mt-4 text-sm text-gray-600">
            Mostrando {filteredCourses.length} de {courses.length} cursos
          </div>
        </div>

        {/* Grid de cursos */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-education mx-auto"></div>
              <p className="text-gray-600 mt-2">Cargando cursos...</p>
            </div>
          ) : filteredCourses.length === 0 ? (
            <div className="p-8 text-center">
              <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No se encontraron cursos con los filtros aplicados</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Curso
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Instructor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nivel
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estudiantes
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredCourses.map((course) => (
                    <tr key={course.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{course.title}</div>
                          <div className="text-sm text-gray-500 truncate max-w-md">{course.description}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {course.instructor_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          course.level === 'principiante' ? 'bg-green-100 text-green-800' :
                          course.level === 'intermedio' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {course.level}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {course.total_students}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {course.is_published ? (
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                            Publicado
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                            Borrador
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          {(hasPermission('manage_courses') || hasPermission('create_course')) && (
                            <Link
                              to={`/admin/cursos/${course.id}/contenido`}
                              className="text-purple-600 hover:text-purple-900"
                              title="Editar Contenido"
                            >
                              <FileEdit className="h-4 w-4" />
                            </Link>
                          )}
                          {hasPermission('publish_course') && (
                            <button
                              onClick={() => handleTogglePublish(course.id, course.is_published)}
                              className={course.is_published ? "text-gray-600 hover:text-gray-900" : "text-green-600 hover:text-green-900"}
                              title={course.is_published ? "Despublicar" : "Publicar"}
                            >
                              {course.is_published ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                          )}
                          {hasPermission('manage_courses') && (
                            <button
                              onClick={() => {
                                setSelectedCourse(course)
                                setModalOpen('edit')
                              }}
                              className="text-blue-600 hover:text-blue-900"
                              title="Editar Metadatos"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          )}
                          {hasPermission('delete_course') && (
                            <button
                              onClick={() => handleDeleteCourse(course.id)}
                              className="text-red-600 hover:text-red-900"
                              title="Eliminar"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {modalOpen === 'create' && (
          <CourseModal
            onClose={() => setModalOpen(null)}
            onSave={handleCreateCourse}
          />
        )}

        {modalOpen === 'edit' && selectedCourse && (
          <CourseModal
            course={selectedCourse}
            onClose={() => {
              setModalOpen(null)
              setSelectedCourse(null)
            }}
            onSave={(data) => handleUpdateCourse(selectedCourse.id, data)}
          />
        )}

        <ConfirmDialog
          isOpen={confirmDialog.isOpen}
          onClose={() => setConfirmDialog({ isOpen: false, courseId: null })}
          onConfirm={confirmDelete}
          title="Eliminar Curso"
          message="¿Estás seguro de que deseas eliminar este curso? Esta acción no se puede deshacer y se perderán todos los datos asociados."
          confirmText="Eliminar"
          cancelText="Cancelar"
          type="danger"
        />
      </div>
    </AdminLayout>
  )
}

function CourseModal({ course, onClose, onSave }: any) {
  const [formData, setFormData] = useState({
    title: course?.title || '',
    description: course?.description || '',
    instructor_name: course?.instructor_name || '',
    level: course?.level || 'principiante',
    duration_hours: course?.duration_hours || 0,
    theme: course?.theme || '',
    is_published: course?.is_published || false
  })

  const handleSubmit = () => {
    if (!formData.title || !formData.description) {
      return
    }
    onSave(formData)
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50" onClick={onClose} />
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">
              {course ? 'Editar Curso' : 'Crear Nuevo Curso'}
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="h-5 w-5" />
            </button>
          </div>
          
          <div className="space-y-4 max-h-96 overflow-y-auto">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Título</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-education focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-education focus:border-transparent"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Instructor</label>
                <input
                  type="text"
                  value={formData.instructor_name}
                  onChange={(e) => setFormData({ ...formData, instructor_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-education focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Duración (horas)</label>
                <input
                  type="number"
                  value={formData.duration_hours}
                  onChange={(e) => setFormData({ ...formData, duration_hours: parseFloat(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-education focus:border-transparent"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nivel</label>
                <select
                  value={formData.level}
                  onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-education focus:border-transparent"
                >
                  <option value="principiante">Principiante</option>
                  <option value="intermedio">Intermedio</option>
                  <option value="avanzado">Avanzado</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tema</label>
                <input
                  type="text"
                  value={formData.theme}
                  onChange={(e) => setFormData({ ...formData, theme: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-education focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="is_published"
                checked={formData.is_published}
                onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                className="rounded border-gray-300 text-education focus:ring-education"
              />
              <label htmlFor="is_published" className="text-sm text-gray-700">
                Publicar curso inmediatamente
              </label>
            </div>
          </div>

          <div className="flex space-x-3 mt-6">
            <button
              onClick={handleSubmit}
              disabled={!formData.title || !formData.description}
              className="flex-1 bg-education text-white px-4 py-2 rounded-lg hover:bg-education/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {course ? 'Guardar Cambios' : 'Crear Curso'}
            </button>
            <button
              onClick={onClose}
              className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300"
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
